/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import React, { useRef, useState, useEffect, useCallback } from "react";
import { Box } from "@panda/jsx";
import { css, cva } from "@panda/css";

const getScrollbarWidth = (): number => {
	const outer = document.createElement("div");
	outer.style.visibility = "hidden";
	outer.style.overflow = "scroll";
	outer.style.width = "100px";
	outer.style.width = "100px";
	document.body.appendChild(outer);

	const inner = document.createElement("div");
	outer.style.width = "100%";
	outer.style.width = "100%";
	document.body.appendChild(inner);

	const scrollbarWidth = outer.offsetWidth - outer.clientWidth;
	document.body.removeChild(outer);
	return scrollbarWidth;
};

const scrollContainerStyles = css({
	overflow: "auto",
	"&::-webkit-scrollbar": {
		zIndex: "1",
	},
	"&::-webkit-scrollbar-track": {
		bg: "eds.background.elevated.low.default",
		borderBlock: "token(sizes.eds.dimension.1) solid token(colors.eds.border.subtle)",
	},
	"&::-webkit-scrollbar-thumb": {
		bg: "transparent",
		boxShadow: "0 0 0 6px token(colors.eds.palette.transparent.light.035) inset",
		border: "4px solid transparent",
		borderRadius: "eds.borderRadius.8",
	},
});

export const gradientStyles = cva({
	base: {
		position: "absolute",
		width: "60px",
		height: "calc(100% - var(--scrollbar-width, 17px))",
		filter: "blur(2px)",
		pointerEvents: "none",
		transition: `opacity .1s ease`,
	},
	variants: {
		enable: {
			true: {
				opacity: "1",
			},
			false: {
				opacity: "0",
			},
		},
	},
});

export interface ScrollContainerProps {
	children?: React.ReactNode;
	disableGradient?: boolean;
}

/**
 * Expects childrent to have a minWidth. Applies a styled scrollbar and an optional gradient to
 * the side(s) when the scrollbar is present but you're not completely scrolled to that side.
 */

export const ScrollContainer = ({ children, disableGradient }: ScrollContainerProps): React.JSX.Element => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [showLeft, setShowLeft] = useState(false);
	const [showRight, setShowRight] = useState(false);
	const [scrollbarWidth, setScrollbarWidth] = useState<number>(17);

	const checkScroll = useCallback((): void => {
		if (disableGradient) return;
		const el = containerRef.current;
		if (!el) return;

		if (el.scrollWidth > el.clientWidth) {
			setShowLeft(Math.floor(el.scrollLeft) > 0);
			setShowRight(Math.ceil(el.scrollLeft) < el.scrollWidth - el.clientWidth);
		} else {
			setShowLeft(false);
			setShowRight(false);
		}
	}, [disableGradient]);

	useEffect(() => {
		setScrollbarWidth(getScrollbarWidth());
		if (disableGradient) return;
		checkScroll();
		const el = containerRef.current;
		if (!el) return;
		el.addEventListener("scroll", checkScroll);
		window.addEventListener("resize", checkScroll);
		return () => {
			el.removeEventListener("scroll", checkScroll);
			window.removeEventListener("resize", checkScroll);
		};
	}, [checkScroll, disableGradient, setScrollbarWidth]);

	return (
		<Box position="relative" maxWidth="100%">
			<Box className={scrollContainerStyles} ref={containerRef}>
				{children}
				{!disableGradient ? (
					<>
						<Box
							top="0"
							left="-1"
							style={{ "--scrollbar-width": `${scrollbarWidth}px` } as any}
							background={`linear-gradient(to right, token(colors.eds.background.default), transparent)`}
							className={gradientStyles({ enable: showLeft })}
						/>
						<Box
							top="0"
							right="-1"
							style={{ "--scrollbar-width": `${scrollbarWidth}px` } as any}
							background={`linear-gradient(to left, token(colors.eds.background.default), transparent)`}
							className={gradientStyles({ enable: showRight })}
						/>
					</>
				) : null}
			</Box>
		</Box>
	);
};
